import { Alert, Anchor, Button, Group, Text } from '@mantine/core';
import { IconAlertTriangle, IconMask, IconTool } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useBilling } from '@/api/billing';
import { useSession, useStopImpersonation } from '@/api/session';
import { useSystemInfo } from '@/api/system';
import dayjs from '@/lib/dayjs';
import { useCurrentWorkspace } from './WorkspaceContext';

/** Hinweise oberhalb des Inhalts: Impersonation (SYS-03), Wartung (SYS-05), Zahlungsstatus (PLAN-06). */
export function SessionBanners() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { data: session } = useSession();
  const { data: system } = useSystemInfo();
  const stop = useStopImpersonation();
  return (
    <>
      {session?.impersonatorName && (
        <Alert color="orange" variant="filled" mb="md" icon={<IconMask size={18} />} p="xs">
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm">{t('banners.impersonating', { name: ctx.user.name, admin: session.impersonatorName })}</Text>
            <Button size="compact-sm" variant="white" color="orange" loading={stop.isPending} onClick={() => stop.mutate(undefined, { onSuccess: () => window.location.assign('/admin/users') })}>
              {t('banners.stopImpersonation')}
            </Button>
          </Group>
        </Alert>
      )}
      {system?.maintenanceMessage && (
        <Alert color="yellow" mb="md" icon={<IconTool size={18} />} p="xs">
          <Text size="sm">{t('banners.maintenanceAdmin', { message: system.maintenanceMessage })}</Text>
        </Alert>
      )}
      {ctx.isOwner && system?.billingMode === 'MOLLIE' && <BillingBanner />}
    </>
  );
}

function BillingBanner() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { data } = useBilling(ctx.workspaceId);
  if (!data) return null;
  const trialDays = data.trialEndsAt ? dayjs(data.trialEndsAt).diff(dayjs(), 'day') : null;
  if (data.status === 'PAST_DUE') {
    return (
      <Alert color="red" mb="md" icon={<IconAlertTriangle size={18} />} p="xs">
        <Text size="sm">
          {t('banners.pastDue')}{' '}
          <Anchor component={Link} to="/billing" size="sm">
            {t('banners.toBilling')}
          </Anchor>
        </Text>
      </Alert>
    );
  }
  if (data.status === 'TRIALING' && trialDays !== null && trialDays <= 7 && !data.hasMandate) {
    return (
      <Alert color="blue" mb="md" p="xs">
        <Text size="sm">
          {t('banners.trialEnds', { count: Math.max(0, trialDays) })}{' '}
          <Anchor component={Link} to="/billing" size="sm">
            {t('banners.choosePlan')}
          </Anchor>
        </Text>
      </Alert>
    );
  }
  return null;
}
